import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billing } from '../api';
import type { PlanResponse, TeamBillingResponse } from '../api';
import { openCheckout } from '../billing/paddle';
import { teamKey, teamsKey } from './teamQueries';

export const planKey = ['billing', 'plan'] as const;
export const teamBillingKey = (id: string) => ['billing', 'team', id] as const;

/**
 * The seat price, read from the payment provider rather than hard-coded here, so the number on
 * screen cannot drift from the number on the invoice.
 */
export function usePlan() {
  return useQuery({
    queryKey: planKey,
    // It changes about never, and re-fetching it on every tab focus would be silly.
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<PlanResponse> => {
      const { data, error } = await billing.plan();
      if (error) {
        throw error;
      }
      if (!data) {
        throw new Error('The plan could not be loaded.');
      }
      return data;
    },
  });
}

export function useTeamBilling(teamId: string | null) {
  return useQuery({
    queryKey: teamBillingKey(teamId ?? ''),
    enabled: teamId !== null,
    queryFn: async (): Promise<TeamBillingResponse> => {
      const { data, error } = await billing.forTeam({ path: { id: teamId! } });
      if (error) {
        throw error;
      }
      if (!data) {
        throw new Error('That team’s billing could not be loaded.');
      }
      return data;
    },
  });
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Re-asks until the answer arrives, or gives up.
 *
 * Paying and being subscribed are two different moments: the card clears in the overlay, then the
 * provider tells our server about it out of band. That gap is usually under a second and
 * occasionally much longer, so the client cannot simply ask once — which is what it used to do,
 * leaving a paid-up team still being offered a Subscribe button until somebody reloaded the page.
 */
export async function pollUntilSubscribed(
  probe: () => Promise<TeamBillingResponse | undefined>,
  options: {
    attempts?: number;
    delayMs?: number;
    wait?: (ms: number) => Promise<void>;
  } = {},
): Promise<boolean> {
  const { attempts = 15, delayMs = 1500, wait = sleep } = options;

  for (let attempt = 0; attempt < attempts; attempt++) {
    const state = await probe();

    if (state?.hasSubscription) {
      return true;
    }

    // No wait after the final look: nobody is going to see the result of it.
    if (attempt < attempts - 1) {
      await wait(delayMs);
    }
  }

  return false;
}

/**
 * Opens the provider's checkout for a transaction the server created.
 *
 * Nothing about the team travels through the browser: the API attaches the team id to the
 * transaction, and this only knows how to open the result.
 */
export function useCheckout(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string | undefined) => {
      const { data, error } = await billing.checkout({ path: { id: teamId } });
      if (error) {
        throw error;
      }

      // Resolves when the overlay reports the payment done or the customer walks away — not when
      // it finishes rendering.
      const outcome = await openCheckout(data!.transactionId, email);

      await queryClient.invalidateQueries({ queryKey: teamBillingKey(teamId) });

      if (outcome !== 'completed') {
        return { outcome, confirmed: false };
      }

      const confirmed = await pollUntilSubscribed(async () => {
        await queryClient.refetchQueries({ queryKey: teamBillingKey(teamId) });
        return queryClient.getQueryData<TeamBillingResponse>(teamBillingKey(teamId));
      });

      // Seat limits and the library's read-only banner both read from this.
      await queryClient.invalidateQueries({ queryKey: teamsKey });

      return { outcome, confirmed };
    },
  });
}

/**
 * Opens the provider's billing pages.
 *
 * `paymentMethod` goes straight to this team's own subscription. That distinction matters because
 * the portal home lists every subscription the payer holds, each labelled with the same product
 * name — so somebody running three teams is looking at three identical cards with no way to tell
 * which is which. Arriving at the right one removes the question rather than answering it.
 */
export function usePortal(teamId: string) {
  return useMutation({
    mutationFn: async (target: 'overview' | 'paymentMethod' = 'overview') => {
      const { data, error } = await billing.portal({ path: { id: teamId } });
      if (error) {
        throw error;
      }

      // Falls back to the overview: the deep link is absent when there is no subscription to
      // point at, and a button that does nothing is worse than one that does something general.
      const url =
        target === 'paymentMethod' ? (data!.updatePaymentMethodUrl ?? data!.url) : data!.url;

      window.open(url, '_blank', 'noopener,noreferrer');
    },
  });
}

export function useCancelPlan(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await billing.cancel({ path: { id: teamId } });
      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamBillingKey(teamId) });
      void queryClient.invalidateQueries({ queryKey: teamKey(teamId) });
      void queryClient.invalidateQueries({ queryKey: teamsKey });
    },
  });
}

/** "£5.00" from 500 and "GBP". */
export function formatPrice(amountMinorUnits: number, currencyCode: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode,
  }).format(amountMinorUnits / 100);
}
